<template>
  <model-form
    ref="modelForm"
    :model="movie"
    :title="`Edit Movie Terms (${movie.id})`"
    :custom-save-function="save"
  >
    <div class="row q-col-gutter-md">
      <model-form-input
        field="name"
        class="col-3"
        label="Name"
        readonly
      />
      <model-form-input
        field="abbreviation"
        class="col-3"
        label="Abbreviation"
        maxlength="4"
        bottom-slots
      />
      <div class="col-3" />
      <div class="col-3 q-py-none q-my-auto">
        <q-btn
          v-if="$isPermitted($permissions.FilmProgramming.View)"
          class="q-pa-sm"
          style="height: 40px; width: 100%;"
          label="Movie"
          icon-right="launch"
          dense
          flat
          @click="$router.push(`/settings/programming/movies/${movie.id}`)"
        />
      </div>
      <model-form-input
        field="dateOfFirstShowing"
        class="col-4"
        label="Date of First Showing"
        :disable="!movie.overrideDateOfFirstShowing"
      />
      <model-form-toggle
        field="overrideDateOfFirstShowing"
        class="col-2 q-mb-md"
        label="Override"
        @update:model-value="toggleOverrideDateOfFirstShowing"
      />
      <model-form-select
        field="boxOfficeWeekStartDay"
        class="col-3"
        label="Box Office Week Start"
        :options="$constants.dayOfWeekNumbers"
        map-options
        emit-value
        bottom-slots
        :hint="movie.reportingDue && changedBoxOfficeWeekStartDay ? 'Changes will not auto-apply.' : undefined"
      >
        <q-tooltip>
          Generate new Box Office Reports using this start day. Does not affect existing Box Office Reports.
          <br>If Box Office Reports have already been generated and sent for this movie, they must be edited manually.
        </q-tooltip>
      </model-form-select>
      <model-form-toggle
        field="boxOfficeWeekExtendPremieres"
        class="col-3 q-pb-md"
        label="Box Office Week Extend Premieres"
      >
        <q-tooltip>
          Allow the Box Office Report for Week 1 to be extended to include premiere showings.
          <br>Will extend the first week by 1-2 days based on the configured Date Of First Showing.
        </q-tooltip>
      </model-form-toggle>
    </div>
    <div
      v-if="movie.isMarathon"
      class="q-mt-md items-center"
    >
      <span>
        Marathon movies do not support distribution terms.
      </span>
    </div>
    <div
      v-else
      class="row q-gutter-y-md items-center"
    >
      <movie-term-table
        class="col-12"
        :movie="movie"
        :library-collection="$_.isNil(movie.libraryParentId) ? null : libraryMovie?.movieTerms"
      />
      <q-tabs
        v-model="tab"
        class="col-12 text-grey"
        active-color="primary"
        indicator-color="primary"
        inline-label
        align="justify"
      >
        <q-tab
          name="Box Office Reports"
          label="Box Office Reports"
        />
        <q-tab
          name="Box Office Settlements"
          label="Box Office Settlements"
        />
      </q-tabs>
      <q-tab-panels
        v-model="tab"
        class="col-12 row"
      >
        <q-tab-panel
          name="Box Office Reports"
          class="col-12 row"
        >
          <box-office-report-table
            class="col-12"
            :movie="movie"
            :initial-selection="adjustedBoxOfficeReports"
            :column-fn="processSubTableColumns"
          />
        </q-tab-panel>
        <q-tab-panel
          name="Box Office Settlements"
          class="col-12 row"
        >
          <box-office-report-settlement-table
            class="col-12"
            :movie="movie"
            :initial-selection="adjustedBoxOfficeReportSettlements"
            :column-fn="processSubTableColumns"
          />
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </model-form>
</template>

<script>
import BoxOfficeReportTable from 'src/modules/admin/box-office-report/BoxOfficeReportTable'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import MovieTermTable from 'src/modules/admin/movie/MovieTermTable'
import BoxOfficeReportSettlementTable from 'src/modules/admin/settlement/BoxOfficeReportSettlementTable'

export default {
  name: 'MovieTermsForm',
  components: {
    BoxOfficeReportTable,
    BoxOfficeReportSettlementTable,
    ModelForm,
    MovieTermTable
  },
  configuration: ['currentCircuit', 'currentSite'],
  models: {
    movie: {
      fields: [
        'id',
        'name',
        'urlSlug',
        'color',
        'releaseDate',
        'dateOfFirstShowing',
        'overrideDateOfFirstShowing',
        'boxOfficeWeekStartDay',
        'boxOfficeWeekExtendPremieres',
        'reportingDue'
      ],
      movieTerms: {
        movie: {
          fields: ['id', 'name', 'dateOfFirstShowing']
        },
        movieTermWeeks: {
          boxOfficeReports: {
            fields: ['id'],
            boxOfficeReportSettlements: {
              fields: ['id']
            }
          },
          locked: true
        },
        showingBadges: 'default',
        notes: true,
        showingCount: true,
        locked: true,
        libraryParentId: true,
        receiveLibraryUpdates: true
      },
      editing: true,
      libraryChildSiteIds: true
    },
    libraryMovie: {
      queryName: 'movie',
      fields: ['id', 'name'],
      movieTerms: {
        showingBadges: 'default'
      },
      variables () {
        return {
          id: this.movie?.libraryParentId,
          skip: _.isNil(this.movie?.libraryParentId)
        }
      }
    }
  },
  data () {
    return {
      tab: 'Box Office Reports'
    }
  },
  computed: {
    changedBoxOfficeWeekStartDay () {
      return !_.isNil(_.find(this.movie.fieldsChanged, { field: 'boxOfficeWeekStartDay' }))
    },
    isMovieTermsDirty () {
      return _.some(this.movie.movieTerms.data, movieTerm => {
        return movieTerm.isLocal || movieTerm.isDirty
      })
    },
    originalMovieTermWeeks () {
      const originalMovieTermWeeks = {}

      _.each(this.movie.originalData.movieTerms, originalMovieTermData => {
        originalMovieTermWeeks[originalMovieTermData.id] = {
          preview: {},
          release: {}
        }

        _(originalMovieTermData.movieTermWeeks).sortBy('weekNumber').each(movieTermWeek => {
          originalMovieTermWeeks[originalMovieTermData.id][movieTermWeek.type][movieTermWeek.id] = movieTermWeek
        })
      })

      return originalMovieTermWeeks
    },
    adjustedBoxOfficeReports () {
      const adjustedBoxOfficeReports = []

      _.each(this.movie.movieTerms.data, movieTerm => {
        _(movieTerm.movieTermWeeks.data).each(movieTermWeek => {
          if (movieTermWeek.isLocal) return

          if (movieTermWeek.isDirty) {
            adjustedBoxOfficeReports.push(movieTermWeek.boxOfficeReports?.data)
          } else if (this.isMovieTermWeekChanged(movieTerm, movieTermWeek) || this.isLastMovieTermWeekChanged(movieTerm, movieTermWeek)) {
            adjustedBoxOfficeReports.push(movieTermWeek.boxOfficeReports?.data)
          }
        })
      })

      return _.flatten(adjustedBoxOfficeReports)
    },
    adjustedBoxOfficeReportSettlements () {
      return _(this.adjustedBoxOfficeReports).map(boxOfficeReport => boxOfficeReport.boxOfficeReportSettlements?.data).flatten().value()
    }
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    processSubTableColumns (columns) {
      const rejectColumns = ['movie', 'studio']
      if (!this.currentSite.isLibrary) {
        rejectColumns.push('site')
      }

      return _.reject(columns, column => _.includes(rejectColumns, column?.name || column?.field))
    },
    isMovieTermWeekChanged (movieTerm, movieTermWeek) {
      const originalMovieTermWeek = this.originalMovieTermWeeks[movieTerm.id][movieTermWeek.type][movieTermWeek.id]

      if (originalMovieTermWeek.termPercentHundredths !== movieTermWeek.termPercentHundredths ||
        originalMovieTermWeek.weekNumber !== movieTermWeek.weekNumber) {
        return true
      }

      return false
    },
    isLastMovieTermWeekChanged (movieTerm, movieTermWeek) {
      const wasLastMovieTermWeek = this.originalMovieTermWeeks[movieTerm.id][movieTermWeek.type].length === movieTermWeek.weekNumber
      if (!wasLastMovieTermWeek) return false

      const lastMovieTermWeek = movieTermWeek.type === 'preview' ? _.last(movieTerm.previewMovieTermWeeks) : _.last(movieTerm.releaseMovieTermWeeks)
      if (lastMovieTermWeek.id !== movieTermWeek.id) return true

      return false
    },
    toggleOverrideDateOfFirstShowing (value) {
      this.movie.dateOfFirstShowing ||= value && this.movie.releaseDate
    },
    async save () {
      return await this.movie.addOrUpdateMovieTerms()
    }
  }
}
</script>
